<template>
  <v-card class="orders-card" elevation="4">
    <v-card-title>
      <h2 class="section-title">Bestellungen</h2>
    </v-card-title>
    <v-card-text>
      <v-row class="order-stats">
        <v-col cols="6" v-for="(stat, index) in orderStats" :key="index">
          <v-hover v-slot:default="{ hover }">
            <v-card
              :elevation="hover ? 8 : 0"
              :class="['order-stat-card', hover ? 'hovered' : '']"
              :color="stat.color"
            >
              <v-card-text class="pa-3">
                <div class="order-stat-value">{{ stat.value }}</div>
                <div class="order-stat-label">{{ stat.label }}</div>
                <v-icon class="order-stat-icon">{{ stat.icon }}</v-icon>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>
      
      <!-- Bestellungsbereiche -->
      <div class="mt-4">
        <h3 class="metric-title">Bestellungen nach Marktplatz</h3>
        <v-list dense dark class="marketplace-list pa-0" rounded>
          <v-list-item
            v-for="(value, key) in orders.openOrders"
            :key="key"
            class="marketplace-item"
          >
            <v-list-item-avatar>
              <v-avatar size="32" :color="getMarketplaceColor(key)">
                <span class="white--text text-uppercase">{{ key.charAt(0) }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ key }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip color="primary" class="marketplace-count">{{ value }}</v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
      
      <!-- Bestellungsstatistik als Balkendiagramm -->
      <div class="mt-4 order-bar-chart-container">
        <h3 class="metric-title mb-3">Bestellungsstatistik</h3>
        <apexchart
          v-if="barChartSeries.length > 0"
          type="bar"
          height="200"
          :options="barChartOptions"
          :series="barChartSeries"
        ></apexchart>
      </div>
      
      <!-- Bestellungschart -->
      <div class="mt-4">
        <h3 class="metric-title mb-2">Bestellungsübersicht</h3>
        <apexchart
          v-if="series.length > 0"
          type="donut"
          height="250"
          :options="chartOptions"
          :series="series"
        ></apexchart>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'OrdersCard',
  
  components: {
    apexchart: VueApexCharts,
  },
  
  props: {
    orders: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'donut',
          background: 'transparent'
        },
        labels: [],
        theme: {
          mode: 'dark'
        },
        colors: ['#00E396', '#FEB019', '#FF4560', '#775DD0', '#00D9FF'],
        legend: {
          position: 'bottom',
          labels: {
            colors: '#fff'
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#fff']
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%'
            }
          }
        }
      },
      series: [],
      
      barChartOptions: {
        chart: {
          type: 'bar',
          background: 'transparent',
          toolbar: { show: false }
        },
        theme: {
          mode: 'dark'
        },
        xaxis: {
          categories: ['Offen', 'Nicht verpackt', 'Abgeschlossen'],
          labels: {
            style: { colors: '#fff' }
          }
        },
        yaxis: {
          labels: {
            style: { colors: '#fff' }
          }
        },
        colors: ['#00E396', '#FEB019', '#FF4560'],
        dataLabels: {
          enabled: false
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: false,
          }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)'
        }
      },
      barChartSeries: []
    };
  },
  
  computed: {
    orderStats() {
      if (!this.orders) return [];
      
      return [
        {
          label: 'Offene Bestellungen',
          value: this.orders.openOrdersTotal || 0,
          icon: 'mdi-package-variant',
          color: 'rgba(0, 227, 150, 0.1)'
        },
        {
          label: 'Nicht verpackt',
          value: this.orders.unpackedOrders || 0,
          icon: 'mdi-package-variant-closed',
          color: 'rgba(254, 176, 25, 0.1)'
        },
        {
          label: 'Abgeschlossen',
          value: this.orders.finishedOrders || 0,
          icon: 'mdi-check-circle',
          color: 'rgba(255, 69, 96, 0.1)'
        },
        {
          label: 'Gesamt heute',
          value: (this.orders.openOrdersTotal || 0) + (this.orders.finishedOrders || 0),
          icon: 'mdi-sigma',
          color: 'rgba(119, 93, 208, 0.1)'
        }
      ];
    }
  },
  
  watch: {
    orders: {
      immediate: true,
      deep: true,
      handler(newOrders) {
        if (newOrders) {
          this.updateCharts();
        }
      }
    }
  },
  
  methods: {
    updateCharts() {
      // Update donut chart
      if (this.orders.openOrders) {
        const labels = Object.keys(this.orders.openOrders);
        const values = Object.values(this.orders.openOrders);
        
        this.chartOptions = {
          ...this.chartOptions,
          labels: labels
        };
        this.series = values;
      }
      
      // Update bar chart
      this.barChartSeries = [{
        name: 'Bestellungen',
        data: [
          this.orders.openOrdersTotal || 0,
          this.orders.unpackedOrders || 0,
          this.orders.finishedOrders || 0
        ]
      }];
    },
    
    getMarketplaceColor(marketplace) {
      const colors = {
        'amazon': 'orange',
        'shopify': 'green',
        'acs': 'blue',
        'muckelmeister': 'red',
        'prime': 'purple',
        'etsy': 'orange',
      };
      return colors[marketplace] || 'grey';
    }
  }
};
</script>

<style scoped>
.orders-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.order-stats {
  margin-bottom: 16px;
}

.order-stat-card {
  position: relative;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.order-stat-card.hovered {
  transform: translateY(-4px);
}

.order-stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #fff;
}

.order-stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.order-stat-icon {
  position: absolute;
  right: 12px;
  top: 12px;
  opacity: 0.3;
  font-size: 2rem;
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.marketplace-list {
  background: rgba(0, 0, 0, 0.2);
}

.marketplace-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.marketplace-item:last-child {
  border-bottom: none;
}

.marketplace-count {
  font-weight: bold;
}
</style>